import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { Seo, faqLd, orgLd } from "@/components/common/Seo";
import { Section, SectionHead, ArrowLink, FaqList, CtaBand, Sev } from "@/components/common/Bits";
import { Reveal } from "@/components/common/Reveal";
import { Hero } from "@/components/home/Hero";
import { serviceIcons, stepIcons, industryIcons, IconTile } from "@/components/common/icons";
import { RiskMatrix, SeverityTable, PricingGrid, CurrencySwitch, useCurrency } from "@/components/blocks/Blocks";
import { principalCategories, services } from "@/content/services";
import { launchRisks, methodologySteps, industries, faqs } from "@/content/general";
import { countries } from "@/content/countries";
import { sampleReport } from "@/content/sampleReport";
import { Stats } from "@/components/proof/Stats";
import { ClientLogos } from "@/components/proof/ClientLogos";
import { Testimonials } from "@/components/proof/Testimonials";
import { site } from "@/config/site";

const websiteLd = { "@context": "https://schema.org", "@type": "WebSite", name: site.name, url: `${site.domain}/`, publisher: { "@id": `${site.domain}/#organization` } };

const Problem = () => (
  <Section testid="problem-section">
    <div className="grid gap-12 lg:grid-cols-12">
      <div className="lg:col-span-5">
        <div className="lg:sticky lg:top-32">
          <SectionHead eyebrow="The problem" title="Most launch failures are predictable. Few are predicted." lede="Teams ship under pressure, and the things that hurt most after launch are rarely exotic. They are the gaps nobody independently checked." className="mb-8" />
          <img src="/img/security.webp" alt="Security engineer testing an application at night" loading="lazy" width="1400" height="934" className="aspect-[4/3] w-full rounded-2xl object-cover" />
        </div>
      </div>
      <ol className="lg:col-span-7">
        {launchRisks.map((r, i) => (
          <Reveal as="li" key={r} delay={i * 0.03} className="group flex items-baseline gap-6 border-b border-line py-5 first:border-t">
            <span className="font-mono text-xs text-teal">{String(i + 1).padStart(2, "0")}</span>
            <span className="text-xl font-bold transition-transform duration-300 group-hover:translate-x-2 sm:text-2xl">{r}</span>
          </Reveal>
        ))}
      </ol>
    </div>
  </Section>
);

const Assess = () => (
  <Section className="bg-white" testid="assess-section">
    <SectionHead eyebrow="What we assess" title="Eight lenses. One evidence-led view of readiness." lede="Each category can be assessed on its own or combined into a full launch-readiness engagement." />
    <div className="grid gap-px border border-line bg-line sm:grid-cols-2 lg:grid-cols-4">
      {principalCategories.map((c, i) => (
        <Reveal key={c.title} delay={(i % 4) * 0.06} className="bg-white">
          <Link to={c.to} className="group relative flex h-full min-h-[170px] flex-col gap-5 overflow-hidden p-7 transition-colors duration-300 hover:bg-ink sm:min-h-[220px]" data-testid={`category-${i}`}>
            <span className="font-mono text-xs text-teal group-hover:text-teal-bright">{String(i + 1).padStart(2, "0")}</span>
            <div>
              <IconTile icon={serviceIcons[c.to.split("/").pop()]} className="mb-5 group-hover:bg-white/10 group-hover:text-teal-bright" />
              <h3 className="text-xl font-extrabold leading-tight group-hover:text-white">{c.title}</h3>
              <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-teal opacity-0 transition-opacity duration-300 group-hover:text-teal-bright group-hover:opacity-100 group-focus-visible:opacity-100">Explore <ArrowUpRight className="h-4 w-4" /></span>
            </div>
          </Link>
        </Reveal>
      ))}
    </div>
  </Section>
);

const Method = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start 80%", "end 60%"] });
  const w = useTransform(scrollYProgress, [0, 1], ["0%", "100%"]);
  return (
    <Section dark testid="methodology-section">
      <SectionHead eyebrow="Our methodology" title="Discover. Assess. Prioritize. Verify. Report." lede="A repeatable five-step process that turns assumptions into evidence." />
      <div ref={ref} className="relative">
        <div className="absolute left-0 right-0 top-0 hidden h-px bg-white/10 lg:block" aria-hidden><motion.div style={{ width: w }} className="h-px bg-teal-bright" /></div>
        <ol className="grid gap-10 lg:grid-cols-5 lg:gap-8">
          {methodologySteps.map((s, i) => (
            <Reveal as="li" key={s.n} delay={i * 0.08} className="border-l border-white/10 pl-6 lg:border-l-0 lg:pl-0 lg:pt-10">
              <IconTile icon={stepIcons[i]} dark className="mb-4" />
              <span className="font-mono text-sm text-teal-bright">{s.n}</span>
              <h3 className="mt-3 text-2xl font-extrabold">{s.title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-slate-300">{s.text}</p>
            </Reveal>
          ))}
        </ol>
      </div>
      <ArrowLink to="/methodology" className="mt-14 !text-teal-bright" testid="methodology-link">Read the full methodology</ArrowLink>
    </Section>
  );
};

const Severity = () => (
  <Section testid="severity-section">
    <div className="grid gap-14 lg:grid-cols-12">
      <div className="lg:col-span-5"><SectionHead eyebrow="Risk severity" title="Every finding earns its rating." lede="Severity combines likelihood, impact and business context, so remediation effort goes where it reduces the most risk." className="mb-10" /><RiskMatrix /></div>
      <div className="lg:col-span-7 lg:pt-24"><SeverityTable /></div>
    </div>
  </Section>
);

const Featured = () => (
  <Section className="bg-white" testid="featured-services">
    <div className="flex flex-wrap items-end justify-between gap-6"><SectionHead eyebrow="Featured services" title="Assessments built for real launch decisions." className="mb-10" /><ArrowLink to="/services" className="mb-12" testid="featured-all-services">All 16 services</ArrowLink></div>
    <div className="border-t border-line">
      {services.filter((s) => ["security-vulnerability-assessment", "privacy-data-protection", "launch-readiness", "performance-speed", "ai-security-governance", "payments-ecommerce"].includes(s.slug)).map((s) => (
        <Reveal key={s.slug}>
          <Link to={`/services/${s.slug}`} className="group grid items-center gap-3 border-b border-line py-7 sm:grid-cols-[64px_1fr_1fr_40px] sm:gap-8" data-testid={`featured-${s.slug}`}>
            <IconTile icon={serviceIcons[s.slug]} />
            <h3 className="text-2xl font-extrabold transition-transform duration-500 group-hover:translate-x-2 sm:text-3xl">{s.title}</h3>
            <p className="text-sm leading-relaxed text-slate-600 line-clamp-3">{s.short}</p>
            <ArrowRight className="hidden h-6 w-6 text-slate-300 transition-[transform,color] duration-300 group-hover:-rotate-45 group-hover:text-teal sm:block" aria-hidden />
          </Link>
        </Reveal>
      ))}
    </div>
  </Section>
);

const Pricing = () => {
  const [cur, setCur] = useCurrency();
  return (
    <Section testid="pricing-preview">
      <div className="flex flex-wrap items-end justify-between gap-6"><SectionHead eyebrow="Packages" title="Clear starting points. Scoped precisely." lede="Starting prices for five common engagements. Every quote is confirmed in a written proposal." className="mb-10" /><div className="mb-12"><CurrencySwitch cur={cur} setCur={setCur} /></div></div>
      <PricingGrid cur={cur} />
      <p className="mt-6 text-xs text-slate-500">USD figures are reference prices; the actual quote depends on scope and agreed currency. <Link to="/pricing" className="font-semibold text-teal link-u">Scope, add-ons and exclusions</Link></p>
    </Section>
  );
};

const Coverage = () => (
  <Section dark testid="coverage-section">
    <div className="grid gap-16 lg:grid-cols-2">
      <div>
        <SectionHead eyebrow="Industries" title="Sector-aware assessments." className="mb-8" />
        <ul className="grid grid-cols-1 gap-px bg-white/10 sm:grid-cols-2">
          {industries.map((ind) => <li key={ind.id} className="flex gap-4 bg-ink p-5"><IconTile icon={industryIcons[ind.id]} dark className="h-10 w-10" /><div><Link to={`/industries#${ind.id}`} className="font-semibold hover:text-teal-bright">{ind.name}</Link><p className="mt-1 text-xs text-slate-400">{ind.risks[0]}</p></div></li>)}
        </ul>
      </div>
      <div>
        <SectionHead eyebrow="Country & regulatory coverage" title="Mapped against official sources." className="mb-8" />
        <img src="/img/globe.webp" alt="Digital globe showing international network connections" loading="lazy" width="1400" height="934" className="mb-6 aspect-[16/7] w-full rounded-2xl object-cover" />
        <ul className="border-t border-white/10">
          {countries.map((c) => (
            <li key={c.slug}><Link to={`/compliance/${c.slug}`} className="group flex items-center justify-between border-b border-white/10 py-4" data-testid={`coverage-${c.slug}`}><span className="text-lg font-bold group-hover:text-teal-bright">{c.name}</span><span className="hidden max-w-[50%] truncate text-xs text-slate-400 sm:block">{c.laws[0].name}</span></Link></li>
          ))}
        </ul>
        <p className="mt-5 text-xs text-slate-400">No generic audit ensures worldwide compliance. We identify likely requirements and refer legal questions to qualified counsel.</p>
      </div>
    </div>
  </Section>
);

const ReportPreview = () => (
  <Section className="bg-white" testid="report-preview">
    <div className="grid items-center gap-14 lg:grid-cols-12">
      <div className="min-w-0 lg:col-span-5">
        <SectionHead eyebrow="Report preview" title="Findings you can act on — and evidence you can show." lede="Executive summary, scope, severity-rated findings register, evidence references, remediation guidance, retest status and a clear readiness recommendation." className="mb-8" />
        <Link to="/sample-report" className="btn-ink" data-testid="report-preview-cta">View the sample report <ArrowRight className="h-4 w-4" /></Link>
      </div>
      <Reveal className="min-w-0 lg:col-span-7">
        <div className="relative border border-line bg-paper p-5 shadow-[0_40px_80px_-40px_rgba(10,22,40,.35)] sm:p-8">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-ink pb-4">
            <div><p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Assessment report</p><p className="font-bold">{sampleReport.product}</p></div>
            
          </div>
          <div className="mt-4 overflow-x-auto">
            <table className="w-full min-w-[480px] text-left text-sm">
              <thead><tr className="text-xs text-slate-500"><th className="py-2 font-medium">ID</th><th className="font-medium">Finding</th><th className="font-medium">Severity</th><th className="font-medium">Status</th></tr></thead>
              <tbody>{sampleReport.findings.slice(0, 5).map((f) => <tr key={f.id} className="border-t border-line"><td className="py-3 font-mono text-xs">{f.id}</td><td className="pr-3">{f.title}</td><td><Sev level={f.sev} /></td><td className="text-xs font-semibold">{f.status}</td></tr>)}</tbody>
            </table>
          </div>
          <div className="mt-5 flex items-center justify-between border-t border-ink pt-4"><span className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Recommendation</span><span className="font-bold text-teal">{sampleReport.recommendation}</span></div>
        </div>
      </Reveal>
    </div>
  </Section>
);

const Faq = () => (
  <Section testid="home-faq">
    <div className="grid gap-12 lg:grid-cols-12">
      <div className="lg:col-span-4"><SectionHead eyebrow="FAQ" title="Straight answers." className="mb-6" /><ArrowLink to="/faq">All questions</ArrowLink></div>
      <div className="lg:col-span-8"><FaqList faqs={faqs.slice(0, 6)} idPrefix="home-faq" /></div>
    </div>
  </Section>
);

export default function Home() {
  return (
    <>
      <Seo title="Rigorfield — Independent Security, Compliance & Launch Readiness Assessments" description="Independent security, privacy, compliance and performance assessments for websites, SaaS, mobile apps, APIs and AI products. Know the risks. Prove the readiness." path="/" jsonLd={[orgLd, websiteLd, faqLd(faqs.slice(0, 6))]} />
      <Hero />
      <Stats />
      <Problem />
      <Assess />
      <Method />
      <Severity />
      <Featured />
      <Pricing />
      <Coverage />
      <ReportPreview />
      <Testimonials />
      <ClientLogos />
      <Faq />
      <CtaBand testid="final-cta" />
    </>
  );
}
